/**
 * Cover — album cover renderer.
 *
 * If the library entry has `coverImage`, renders the real artwork.
 * Otherwise falls back to a generative type-on-color placeholder using one
 * of six type-style variants:
 *   stencil    — Radiohead-ish editorial
 *   carved     — Metallica metal weight
 *   bold-serif — Kendrick formal italic
 *   soft-serif — Fleetwood Mac '70s
 *   typewriter — NMH lo-fi
 *   wide-display — Tool / K-pop
 */

import type { CSSProperties } from 'react';
import type { AlbumLibraryEntry } from '@/types/album';

interface CoverProps {
  album: AlbumLibraryEntry;
  size?: number;
  /**
   * `eager` for above-the-fold uses (Hero); `lazy` for off-screen tiles
   * (Picker grid). Defaults to lazy.
   */
  loading?: 'eager' | 'lazy';
}

const TYPE_STYLES: Record<AlbumLibraryEntry['typeStyle'], (size: number) => CSSProperties> = {
  stencil: (size) => ({
    fontFamily: 'var(--nyx-font-display)',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: size * 0.13,
    lineHeight: 0.95,
  }),
  carved: (size) => ({
    fontFamily: 'var(--nyx-font-display)',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '-0.01em',
    fontSize: size * 0.16,
    lineHeight: 0.92,
  }),
  'bold-serif': (size) => ({
    fontFamily: 'Georgia, serif',
    fontWeight: 700,
    textTransform: 'none',
    letterSpacing: '-0.02em',
    fontSize: size * 0.14,
    lineHeight: 1.0,
    fontStyle: 'italic',
  }),
  'soft-serif': (size) => ({
    fontFamily: 'Georgia, serif',
    fontWeight: 400,
    textTransform: 'none',
    letterSpacing: '0.01em',
    fontSize: size * 0.12,
    lineHeight: 1.05,
  }),
  typewriter: (size) => ({
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    fontSize: size * 0.085,
    lineHeight: 1.15,
  }),
  'wide-display': (size) => ({
    fontFamily: 'var(--nyx-font-display)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    fontSize: size * 0.1,
    lineHeight: 1.0,
  }),
};

export function Cover({ album, size = 320, loading = 'lazy' }: CoverProps): JSX.Element {
  const coverStyle: CSSProperties = {
    width: `var(--ap-cover-size, ${size}px)`,
    height: `var(--ap-cover-size, ${size}px)`,
    background: album.palette.bg,
    color: album.palette.fg,
    ['--ap-accent' as string]: album.palette.accent,
  };

  if (album.coverImage) {
    return (
      <div className="ap-cover ap-cover-image" style={coverStyle} aria-hidden="true">
        <img
          src={album.coverImage}
          alt=""
          width={size}
          height={size}
          loading={loading}
          decoding="async"
          draggable={false}
        />
      </div>
    );
  }

  const titleStyle = TYPE_STYLES[album.typeStyle](size);

  return (
    <div className="ap-cover" style={coverStyle} aria-hidden="true">
      <div className="ap-cover-grain" />
      <div
        className="ap-cover-rule"
        style={{ background: album.palette.fg, opacity: 0.18 }}
      />
      <div className="ap-cover-meta" style={{ color: album.palette.fg }}>
        <span>{album.year}</span>
        <span>·</span>
        <span>{album.genre.toUpperCase()}</span>
      </div>
      <div className="ap-cover-title" style={titleStyle}>
        {album.album}
      </div>
      <div
        className="ap-cover-artist"
        style={{ color: album.palette.fg, opacity: 0.6 }}
      >
        {album.artist}
      </div>
    </div>
  );
}
